import React, { useContext, useEffect, useRef, useState } from 'react'
import { useTheme } from '@mui/material/styles'
import { useSetAtom } from 'jotai'
import { IssueType, WarrantListItem } from '@finance-lab/utils/types'
import { IssueEntity } from 'types/IssueEntity'
import { CompanyEntity, ShareCapitalChange } from 'types/CompanyEntity'
import { BarSeriesType, CurveType, LineSeriesType, YAxis } from '@mui/x-charts/models'
import { useLoaderData, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { ChartsTooltipContainer } from '@mui/x-charts/ChartsTooltip'
import Grid from '@mui/material/Grid'
import { TradesGraphToolbar } from './Toolbar'
import { TradesGraphTooltip } from './Tooltip'
import { mapTimeSelectorKeyToDate, TIME_SELECTOR_ROUTE_PARAM, TradesGraphTimeSelector } from './TimeSelector'
import { InstrumentApi, TradesType } from '../../../../api/InstrumentApi'
import IssueApi from '../../../../api/IssueApi'
import WarrantApi from '../../../../api/WarrantApi'
import { TradeRecord } from 'types/Instrument'
import { useQuery } from '../../../../hooks/useQuery'
import { formatBigMoney, formatBigNumber, formatNumber } from '../../../../packages/utils/numberFormatters'
import InfoBlock from '../../../components/InfoBlock'
import { TimeLineChart } from '../../../../components/TimeLineChart'
import { ChartCompanyEvents } from './CompanyEvents'
import { MouseZoom } from './MouseZoom'
import { IssueSubscriptionPeriod } from './IssueSubscriptionPeriod'
import { UserMeContext } from '../../../../contexts/UserMeContext'
import { CompanyTabs, selectedTabAtom } from './CompanyTabs'
import { alpha } from '@mui/system/colorManipulator'
import {
    barSeriesDropdownItems,
    graphBarSerieRoutingParam,
    graphLineSerieRoutingParam,
    lineSeriesDropdownItems,
} from './constants'
const instrumentApi = new InstrumentApi()
const issueApi = new IssueApi()
const warrantApi = new WarrantApi()

const ISSUE_TYPES: IssueType[] = ['Rights issue', 'ABB Directed issue', 'Directed issue', 'Non-cash issue', 'IPO']
export const SELECTED_ISSUE_ROUTE_PARAM = 'issueId'

export const TradesGraph = ({
    companyId,
    instrumentId,
    instrumentName,
    shareCapitalChanges,
}: {
    companyId: string
    instrumentId: string
    instrumentName: string | null
    shareCapitalChanges: ShareCapitalChange[] | null
}) => {
    const company: CompanyEntity = useLoaderData()
    const [trades, setTrades] = useState<TradesType | null>(null)
    const [issues, setIssues] = useState<IssueEntity[] | null>(null)
    const [warrants, setWarrants] = useState<WarrantListItem[] | null>(null)
    const [isLoading, setIsLoading] = useState(true)
    const { user } = useContext(UserMeContext)
    const userSettings = user?.settings?.tradesGraph
    const [curveType, setCurveType] = useState<CurveType>(userSettings?.curveType ?? 'monotoneX')
    const tabsContainerRef = useRef<HTMLDivElement>(null)
    const theme = useTheme()
    const query = useQuery()
    const { i18n, t } = useTranslation()
    const navigate = useNavigate()
    const setSelectedTab = useSetAtom(selectedTabAtom)

    const defaultLineSerie = userSettings?.serie ?? 'marketcap'
    const selectedLineSerie =
        lineSeriesDropdownItems.find(({ id }) => id === query.get(graphLineSerieRoutingParam))?.id || defaultLineSerie
    const defaultBarSerie = userSettings?.barSerie ?? 'turnoverSek'
    const selectedBarSerie =
        barSeriesDropdownItems.find(({ id }) => id === query.get(graphBarSerieRoutingParam))?.id || defaultBarSerie

    useEffect(() => {
        setTrades(null)
        setIsLoading(true)
        void (async () => {
            try {
                const result = await Promise.allSettled([
                    (async () => {
                        const trades = instrumentApi.getTrades(instrumentId)

                        for await (const t of trades) {
                            setTrades(t)
                        }
                    })(),
                    issueApi
                        .findByCompanyId(companyId, true, { excludeCancelled: true })
                        .then((issues) => setIssues(issues.filter(({ type }) => ISSUE_TYPES.includes(type)))),
                    warrantApi.findByCompanyId(companyId, { excludeCancelled: true }).then(setWarrants),
                ])

                result.filter((r) => r.status === 'rejected').forEach((e) => console.error(e))
            } catch (err) {
                console.error(err)
            } finally {
                setIsLoading(false)
            }
        })()
    }, [companyId, instrumentId])

    const handleSelectEvent = (eventType: 'warrant' | 'issue' | 'split', id: string) => {
        query.set(SELECTED_ISSUE_ROUTE_PARAM, `${eventType}__${id}`)
        setSelectedTab('selected-event')
        navigate(`?${query.toString()}`, { replace: true })

        if (tabsContainerRef.current) {
            const boundingRect = tabsContainerRef.current.getBoundingClientRect()

            if (boundingRect.top + boundingRect.height / 2 > window.innerHeight + window.scrollY) {
                window.scrollTo({ top: boundingRect.top - window.innerHeight / 2 })
            }
        }
    }
    const handleChangeCurveType = (curveType: CurveType) => setCurveType(curveType)

    const selectedAlternative = mapTimeSelectorKeyToDate(query.get(TIME_SELECTOR_ROUTE_PARAM) || '')
    const yAxisValueFormatter: YAxis<'band'>['valueFormatter'] = React.useMemo(
        () => (value: number) => {
            if (value === null) return ''

            if (selectedLineSerie === 'marketcap') {
                return formatBigMoney(value, {
                    locale: i18n.language,
                    decimals: 0,
                })
            }

            if (selectedLineSerie === 'numberOfShares') {
                return formatBigNumber(value, { locale: i18n.language })
            }

            const maxDecimals = value > 1 ? 2 : value < 0.01 ? 3 : 4

            return formatNumber(value, { minDecimals: 0, maxDecimals, locale: i18n.language })
        },
        [i18n.language, selectedLineSerie],
    )

    const { series, xAxisData } = React.useMemo(() => {
        if (!trades) return { series: [], xAxisData: [] }

        const filterByDate = (t: Pick<TradeRecord, 'recordDate'>) =>
            new Date(t.recordDate).getTime() >= selectedAlternative.start &&
            new Date(t.recordDate).getTime() <= selectedAlternative.end

        const xAxisData = (trades?.share.map((d) => new Date(d.recordDate).getTime()) || []).filter(
            (d) => d >= selectedAlternative.start && d <= selectedAlternative.end,
        )

        return {
            xAxisData,
            series: [
                {
                    data: trades.share
                        .filter(filterByDate)
                        .map((t) => (t[selectedLineSerie] ? +t[selectedLineSerie] : null)),
                    type: 'line',
                    id: 'trades',
                    label: instrumentName || t('pages.companies.share'),
                    color: theme.palette.info.main,
                    area: true,
                    connectNulls: true,
                    curve: curveType,
                    yAxisId: 'y-axis-line',
                    xAxisId: 'x-axis-line',
                },
                ...Object.keys(trades.unit).map(
                    (key) =>
                        ({
                            data: trades.unit[key]
                                .filter(filterByDate)
                                .map((t) => (t[selectedLineSerie] ? +t[selectedLineSerie] : null)),

                            type: 'line',
                            id: `units-${key}`,
                            yAxisId: 'y-axis-line',
                            xAxisId: 'x-axis-line',
                            color: theme.palette.FE.main,
                            label: t('pages.companies.btaBtu'),
                            connectNulls: true,
                            curve: curveType,
                        }) as LineSeriesType,
                ),
                ...Object.keys(trades.right).map(
                    (key) =>
                        ({
                            data: trades.right[key]
                                .filter(filterByDate)
                                .map((t) => (t[selectedLineSerie] ? +t[selectedLineSerie] : null)),

                            type: 'line',
                            id: `rights-${key}`,
                            yAxisId: 'y-axis-line',
                            xAxisId: 'x-axis-line',
                            color: theme.palette.FE[200],
                            label: t('pages.companies.rights'),
                            connectNulls: true,
                            curve: curveType,
                        }) as LineSeriesType,
                ),
                {
                    id: 'quantity',
                    data: trades.share
                        .filter(filterByDate)
                        .map((t) => (t[selectedBarSerie] ? t[selectedBarSerie] : null)),
                    color: alpha(theme.palette.info.main, 0.7),
                    yAxisId: 'y-axis-bar',
                    xAxisId: 'x-axis-bar',
                    type: 'bar',
                    label: t('pages.companies.volume'),
                    valueFormatter: (val) => {
                        return !val
                            ? ''
                            : formatNumber(val, {
                                  locale: i18n.language,
                                  decimals: 0,
                                  suffix: ` ${t('charts.units.st')}`,
                              })
                    },
                },
            ] as (LineSeriesType | BarSeriesType)[],
        }
    }, [
        trades,
        instrumentName,
        theme.palette.info.main,
        theme.palette.FE,
        curveType,
        selectedAlternative.start,
        selectedAlternative.end,
        selectedLineSerie,
        selectedBarSerie,
        i18n.language,
        t,
    ])

    const issuesForSelectedInstrument = issues?.filter((i) => i.instrumentId === instrumentId) ?? null
    const warrantsForSelectedInstrument = warrants?.filter((w) => w.shareInstrumentId === instrumentId) ?? null

    if (!isLoading && !trades?.share.length) return null

    return (
        <Grid container spacing={6}>
            <Grid size={{ xs: 12, lg: 6 }}>
                <InfoBlock noPadding>
                    <TimeLineChart
                        height={340}
                        hideTooltip
                        isLoading={isLoading}
                        isSlim
                        series={series}
                        surfaceChildren={issues?.map((i) => (
                            <IssueSubscriptionPeriod
                                key={i.id}
                                issueId={i.id}
                                onClick={() => handleSelectEvent('issue', i.id)}
                            />
                        ))}
                        skipAnimation
                        xAxisData={xAxisData}
                        yAxisValueFormatter={yAxisValueFormatter}
                    >
                        <ChartCompanyEvents
                            isShareCapitalChangesEnabled={selectedLineSerie === 'numberOfShares'}
                            onSelect={(type, id) => handleSelectEvent(type, id)}
                            issues={issuesForSelectedInstrument}
                            shareCapitalChanges={shareCapitalChanges}
                            warrants={warrantsForSelectedInstrument}
                        />
                        <TradesGraphToolbar
                            defaultLineSerie={defaultLineSerie}
                            defaultBarSerie={defaultBarSerie}
                            selectedCurveType={curveType}
                            onChangeCurveType={handleChangeCurveType}
                        />
                        <ChartsTooltipContainer trigger='axis'>
                            <TradesGraphTooltip currency={company.currency} trades={trades} />
                        </ChartsTooltipContainer>
                        <MouseZoom />
                    </TimeLineChart>

                    <TradesGraphTimeSelector isLoading={isLoading} shareTrades={trades?.share ?? null} />
                </InfoBlock>
            </Grid>
            <Grid size={{ xs: 12, lg: 6 }}>
                <div ref={tabsContainerRef} style={{ height: '100%' }}>
                    <CompanyTabs />
                </div>
            </Grid>
        </Grid>
    )
}
